import type { CatalogEntry } from '../types';

/**
 * DWD bulk workspace connect (feature `16`) error codes + catalog.
 *
 * Code shape is `16AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`1600xx`, AA=00) are recurring cross-endpoint conditions —
 * `WorkspaceNotFound`, `AccountNotFound`, `AlreadyConnected`,
 * `ConnectedToAnotherAccount`, `ProviderNotConfigured` — collapsed to ONE code
 * each so the same condition reads the same on every route.
 *
 * Endpoint → `AA` map:
 *   createGoogleWorkspace=01, createMicrosoftWorkspace=02, microsoftConsentUrl=03,
 *   syncPreview=04, sync=05, connectedEmails=06, disconnect=07, delete=08,
 *   reconnect=09, syncStatus=10.
 *
 * Provider-call failures (Directory list, tenant token, JWT impersonation) fold
 * the actionable hint ("re-authorize DWD with these scopes", "admin email not in
 * domain") into the user message at the throw site via `messageOverride`.
 */
export const DWD_WORKSPACE_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    WorkspaceNotFound: '160001',
    AccountNotFound: '160002',
    AlreadyConnected: '160003',
    ConnectedToAnotherAccount: '160004',
    ProviderNotConfigured: '160005',
  },
  /** createGoogleWorkspace=01. */
  CreateGoogle: {
    InvalidAdminEmail: '160101',
    DelegationNotConfigured: '160102',
  },
  /** createMicrosoftWorkspace=02. */
  CreateMicrosoft: {
    StateInvalid: '160201',
    TenantMismatch: '160202',
    ConsentFailed: '160203',
  },
  /** microsoftConsentUrl=03. */
  MicrosoftConsentUrl: {
    DomainNotResolved: '160301',
  },
  /** syncPreview=04. */
  SyncPreview: {
    DirectoryListFailed: '160401',
  },
  /** sync=05. */
  Sync: {
    NothingSelected: '160501',
    /**
     * Per-row job failure, never thrown: the mailbox's insert transaction
     * failed for a reason other than the address being already connected
     * (that one is `EmailAccounts.Shared.EmailAlreadyConnected`).
     */
    InsertFailed: '160502',
  },
  /** syncStatus=10. */
  SyncStatus: {
    JobNotFound: '161001',
  },
} as const;

export const DWD_WORKSPACE_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '160001': { httpStatus: 404, message: "We couldn't find that workspace." },
  '160002': { httpStatus: 404, message: "We couldn't find that mailbox." },
  '160003': {
    httpStatus: 409,
    message: 'This workspace is already connected to your account.',
  },
  '160004': {
    httpStatus: 409,
    message: 'This workspace is already connected to another account.',
  },
  '160005': {
    httpStatus: 503,
    message: 'Bulk workspace connect is not configured. Contact support.',
  },

  // ── Create — Google ──
  '160101': {
    httpStatus: 400,
    message: "We couldn't reach that admin mailbox. Check the admin email.",
  },
  '160102': {
    httpStatus: 400,
    message:
      'Domain-wide delegation is not authorized for the required scopes. Re-authorize in the Google Admin Console and try again.',
  },

  // ── Create — Microsoft ──
  '160201': {
    httpStatus: 400,
    message: 'The consent response was invalid or expired. Please try again.',
  },
  '160202': {
    httpStatus: 403,
    message: 'This consent does not belong to your account.',
  },
  '160203': {
    httpStatus: 400,
    message: 'Microsoft admin consent could not be completed. Please try again.',
  },

  // ── Consent URL — Microsoft ──
  '160301': {
    httpStatus: 422,
    message:
      "We couldn't find a Microsoft tenant for that domain. Check the domain, or omit it to get a generic consent URL.",
  },

  // ── Sync preview ──
  '160401': {
    httpStatus: 502,
    message: "We couldn't list mailboxes for this workspace.",
  },

  // ── Sync ──
  '160501': {
    httpStatus: 400,
    message: 'Select at least one mailbox to connect.',
  },
  '160502': {
    httpStatus: 500,
    message: "We couldn't connect this mailbox. Please try again.",
  },

  // ── Sync status ──
  '161001': { httpStatus: 404, message: "We couldn't find that sync job." },
};
